import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import { allows, useAuth } from "./composables/useAuth";
import "./styles/tokens.css";
import "./styles/prototype.css";
import "./styles/ui.css";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard" },
    { path: "/login", name: "login", component: { template: "<span />" } },
    { path: "/dashboard", name: "dashboard", component: () => import("./views/core/DashboardView.vue") },
    { path: "/tasks", name: "tasks", component: () => import("./views/core/TasksView.vue") },
    { path: "/events", name: "events", component: () => import("./views/core/EventsView.vue") },
    { path: "/audit", name: "audit", component: () => import("./views/core/AuditView.vue") },
    { path: "/approvals", name: "approvals", component: () => import("./views/core/ApprovalsView.vue") },
    { path: "/risk", name: "risk", component: () => import("./views/risk/RiskCenterView.vue") },
    { path: "/coins", redirect: { path: "/risk", query: { tab: "coins" } } },
    { path: "/addresses", redirect: { path: "/risk", query: { tab: "addresses" } } },
    { path: "/exchanges", redirect: { path: "/risk", query: { tab: "exchanges" } } },
    { path: "/files", name: "files", component: () => import("./views/risk/FilesView.vue") },
    { path: "/documents", name: "documents", component: () => import("./views/risk/DocumentsView.vue") },
    { path: "/payroll", name: "payroll", component: () => import("./views/admin/PayrollView.vue") },
    { path: "/notices", name: "notices", component: () => import("./views/admin/NoticesView.vue") },
    { path: "/duty", redirect: "/dashboard" },
    { path: "/reports", name: "reports", component: () => import("./views/admin/ReportsView.vue") },
    { path: "/departments", redirect: { path: "/settings", query: { tab: "departments" } } },
    { path: "/roles", redirect: { path: "/settings", query: { tab: "roles" } } },
    { path: "/users", redirect: { path: "/settings", query: { tab: "users" } } },
    { path: "/settings", name: "settings", component: () => import("./views/admin/SettingsView.vue") },
    { path: "/me", name: "me", component: () => import("./views/admin/ProfileView.vue") },
    { path: "/account", name: "account", component: () => import("./views/admin/AccountView.vue") },
    { path: "/:pathMatch(.*)*", redirect: "/dashboard" }
  ]
});

const routePermissions: Record<string, string> = {
  dashboard: "dashboard.read",
  tasks: "task.read",
  events: "event.read",
  risk: "risk.read",
  approvals: "event.read",
  audit: "audit.read",
  notices: "event.read",
  files: "",
  documents: "file.read",
  payroll: "settings.write",
  reports: "report.read",
  settings: "settings.read"
};
function firstAllowed() {
  const auth = useAuth();
  return Object.keys(routePermissions).find(name => allows(auth.state.user, routePermissions[name] ?? "")) ?? "me";
}
router.beforeEach(async (to) => {
  const auth = useAuth();
  if (!auth.state.ready) await auth.restore();
  if (to.name === "login") return auth.signedIn.value ? { name: "dashboard" } : true;
  if (!auth.signedIn.value) return { name: "login" };
  const needed = routePermissions[String(to.name ?? "")];
  if (needed && !allows(auth.state.user, needed)) {
    const next = firstAllowed();
    if (to.name === next) return true;
    return { name: next };
  }
  return true;
});

createApp(App).use(router).mount("#app");
